import { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { TopBar } from './components/TopBar';
import { LeftPanel } from './components/LeftPanel';
import { LineItemTable } from './components/LineItemTable';
import { RightPanel } from './components/RightPanel';
import { DealLibraryPage } from './components/DealLibraryPage';
import { PasteImportDialog } from './components/PasteImportDialog';
import { VendorTab } from './components/VendorTab';
import { ForecastTab } from './components/ForecastTab';
import { MaintenanceArea } from './components/MaintenanceArea';
import { DealInvoicesPaymentsTab } from './components/DealInvoicesPaymentsTab';
import { DealVendorPaymentsTab } from './components/DealVendorPaymentsTab';
import { DealNotesTab } from './components/DealNotesTab';
import { DealCalendarTab } from './components/DealCalendarTab';
import { MasterPriceListTab } from './components/MasterPriceListTab';
import { ImportMasterPriceListDialog } from './components/ImportMasterPriceListDialog';
import { SettingsBackupTab } from './components/SettingsBackupTab';
import { LoginPage } from './components/LoginPage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog';
import { Textarea } from './components/ui/textarea';
import { useColumnConfig } from './hooks/useColumnConfig';
import * as api from './lib/api';
import { getStoredToken } from './lib/authToken';
import { toChf, toBase, fromBase, chfToPresentation } from './lib/utils';
import './App.css';

/**
 * Normalize an axios error's ``response.data.detail`` into a human-readable
 * message. Handles both common FastAPI shapes:
 *   - 4xx string detail (``{"detail": "calendar_steps[0]: ..."}``)
 *   - 422 validation detail (``{"detail": [{"loc": [...], "msg": "...", ...}]}``)
 * Returns ``fallback`` when no readable message is extractable.
 */
function formatApiErrorDetail(detail) {
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    const parts = detail.slice(0, 3).map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const loc = Array.isArray(entry.loc)
        ? entry.loc.filter((x) => x !== 'body').join('.')
        : '';
      const msg = typeof entry.msg === 'string' ? entry.msg : '';
      if (loc && msg) return `${loc}: ${msg}`;
      return msg || loc || null;
    }).filter(Boolean);
    if (parts.length) {
      const more = detail.length > parts.length ? ` (+${detail.length - parts.length} more)` : '';
      return `${parts.join(' · ')}${more}`;
    }
  }
  return '';
}

function apiErrorMessage(error, fallback = 'Request failed') {
  const msg = formatApiErrorDetail(error?.response?.data?.detail);
  return msg || fallback;
}

const emptyBexioContactForm = {
  contact_type: 'company',
  name_1: '',
  name_2: '',
  email: '',
  phone_fixed: '',
  address: '',
  postcode: '',
  city: '',
};

function forecastBucketBanner(view, bucket) {
  const un = bucket === 'unscheduled';
  if (view === 'timeline') {
    return un
      ? 'Showing deals with no Deal Closure Date — stage-weighted revenue that cannot be placed in any month.'
      : `Showing deals with Deal Closure Date in ${bucket} (stage-weighted revenue).`;
  }
  if (view === 'monthly_performance') {
    if (un) {
      return 'Showing Demand and Quote Issued deals with no Deal Closure Date — stage-weighted revenue that cannot be placed in any month (same as the Unscheduled projected-sales row).';
    }
    return `Showing deals contributing to projected sales and/or actual revenue for ${bucket}: projected is stage-weighted revenue for Demand and Quote Issued only (same as the Projected Sales column — stalled pipeline excluded); actual revenue is Approved, Partial, Invoiced, or Paid with eligible Deal Closure Date (same as the Actual Revenue column).`;
  }
  if (view === 'receivables_month') {
    if (bucket === 'outside_period') {
      return 'Showing deals with open receivables whose expected-paid month falls outside the selected forecast period (rollup of all months before / after the range).';
    }
    return `Showing deals with open receivables due in ${bucket} (tranche expected-paid month; overdue invoices stay anchored to their original due month).`;
  }
  return un
    ? 'Showing deals with no Deal Closure Date — cash-in is unscheduled until a date is set.'
    : `Showing deals whose cash receipt falls in ${bucket} (Deal Closure Date + payment terms).`;
}

function DealEngine() {
  // State
  const [deal, setDeal] = useState(null);
  const dealRef = useRef(null);
  const [deals, setDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [fxRates, setFxRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(true);
  /** When set, deal library shows this slice (from Forecast month drill) instead of the global deals list. */
  const [dealLibraryForecastFilter, setDealLibraryForecastFilter] = useState(null);
  const forecastFilterRef = useRef(null);
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [showImportMasterDialog, setShowImportMasterDialog] = useState(false);
  /** When set, Maintenance tab highlights & scrolls to this maintenance item row (notifications deep-link). */
  const [maintenanceFocusItemId, setMaintenanceFocusItemId] = useState(null);
  const [activeTab, setActiveTab] = useState('deals');
  const [dealWorkspaceTab, setDealWorkspaceTab] = useState('deal');
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [mobileDealPane, setMobileDealPane] = useState('line-items');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionComment, setRejectionComment] = useState('');
  const [issueQuoteDialogOpen, setIssueQuoteDialogOpen] = useState(false);
  const [issueQuoteMode, setIssueQuoteMode] = useState('line_items');
  const [issueQuoteBulkText, setIssueQuoteBulkText] = useState('');
  const [issueQuoteBulkNote, setIssueQuoteBulkNote] = useState('');
  const [issueQuoteSubmitting, setIssueQuoteSubmitting] = useState(false);
  const [issueQuotePdfBusy, setIssueQuotePdfBusy] = useState(false);
  const [issueQuoteStageBusy, setIssueQuoteStageBusy] = useState(false);
  const [downloadQuoteDialogOpen, setDownloadQuoteDialogOpen] = useState(false);
  const [downloadQuoteVatMode, setDownloadQuoteVatMode] = useState('standard');
  const [downloadQuoteLang, setDownloadQuoteLang] = useState('fr');
  const [downloadQuoteBusy, setDownloadQuoteBusy] = useState(false);
  const [downloadQuoteOptions, setDownloadQuoteOptions] = useState(null);
  const [issueContactDialogOpen, setIssueContactDialogOpen] = useState(false);
  const [issueContactSubmitting, setIssueContactSubmitting] = useState(false);
  const [issueContactForm, setIssueContactForm] = useState(emptyBexioContactForm);
  const [pendingQuoteRequest, setPendingQuoteRequest] = useState(null);
  const [lastIssuedQuote, setLastIssuedQuote] = useState(null);
  const [presentationCurrency, setPresentationCurrency] = useState('CHF');
  const [vendorCurrency, setVendorCurrency] = useState('CHF');

  // AI deal advisor (OpenAI via backend)
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [advisorFocus, setAdvisorFocus] = useState('all');
  const [advisorResult, setAdvisorResult] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [advisorError, setAdvisorError] = useState(null);
  const [advisorApplyingKey, setAdvisorApplyingKey] = useState(null);

  // Column config (UI-only, localStorage persisted)
  const columnConfig = useColumnConfig();

  // Keep dealRef always in sync with latest deal state
  useEffect(() => { dealRef.current = deal; }, [deal]);

  useEffect(() => {
    setAdvisorResult(null);
    setAdvisorError(null);
    setAdvisorApplyingKey(null);
  }, [deal?.id]);

  useEffect(() => {
    forecastFilterRef.current = dealLibraryForecastFilter;
  }, [dealLibraryForecastFilter]);

  useEffect(() => {
    if (!maintenanceFocusItemId) return undefined;
    const t = window.setTimeout(() => setMaintenanceFocusItemId(null), 12000);
    return () => window.clearTimeout(t);
  }, [maintenanceFocusItemId]);

  // Deals tab without a loaded deal should always show the library, not an empty workspace.
  useEffect(() => {
    if (activeTab === 'deals' && !deal) {
      setShowLoadDialog(true);
    }
  }, [activeTab, deal]);

  // Undo / Redo state (session-level deal history)
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Set deal with undo tracking (for mutations only, not load/create)
  const setDealWithUndo = useCallback((newDeal) => {
    setDeal(prev => {
      if (prev) {
        setUndoStack(s => [...s.slice(-49), JSON.parse(JSON.stringify(prev))]);
        setRedoStack([]);
      }
      return newDeal;
    });
  }, []);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0 || !deal) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack(r => [...r, JSON.parse(JSON.stringify(deal))]);
    setUndoStack(s => s.slice(0, -1));
    setDeal(prev);
    toast.info('Undo');
  }, [undoStack, deal]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0 || !deal) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack(s => [...s, JSON.parse(JSON.stringify(deal))]);
    setRedoStack(r => r.slice(0, -1));
    setDeal(next);
    toast.info('Redo');
  }, [redoStack, deal]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo]);

  // Fetch FX rates on mount and periodically
  useEffect(() => {
    const fetchFX = async () => {
      try {
        const response = await api.getFXRates();
        setFxRates(response.data);
      } catch (error) {
        console.error('Failed to fetch FX rates:', error);
      }
    };

    fetchFX();
    const interval = setInterval(fetchFX, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.getCategories();
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch vendors on mount
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await api.getVendors();
        setVendors(response.data);
      } catch (error) {
        console.error('Failed to fetch vendors:', error);
      }
    };
    fetchVendors();
  }, []);

  // Fetch deals list
  const fetchDeals = useCallback(async () => {
    try {
      const response = await api.getDeals();
      setDeals(response.data);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  // Load a specific deal
  const reloadCurrentDeal = useCallback(async () => {
    if (!deal?.id) return;
    try {
      const response = await api.getDeal(deal.id);
      setDeal(response.data);
    } catch (error) {
      console.error('Failed to refresh deal:', error);
    }
  }, [deal?.id]);

  const loadDeal = useCallback(async (dealId, opts = {}) => {
    const { workspaceTab = 'deal' } = opts;
    setLoading(true);
    try {
      const response = await api.getDeal(dealId);
      setDeal(response.data);
      setUndoStack([]);
      setRedoStack([]);
      setShowLoadDialog(false);
      setDealLibraryForecastFilter(null);
      setActiveTab('deals');
      setDealWorkspaceTab(workspaceTab);
      setMobileDealPane('line-items');
      toast.success('Deal loaded');
    } catch (error) {
      console.error('Failed to load deal:', error);
      toast.error('Failed to load deal');
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new deal
  //
  // UX contract: clicking the "New Deal" CTA must land the user directly on
  // the new deal's workspace in full screen — NOT leave them stranded on the
  // Deal Library / Forecast / wherever they were. This means we must:
  //   1. Close the Deal Library modal view (showLoadDialog).
  //   2. Clear any active library sub-filter (e.g. forecast drilldown).
  //   3. Switch to the deals tab and the 'deal' sub-tab.
  //   4. Prepend the created deal to the cached list so the library isn't
  //      stale if the user navigates back.
  const createNewDeal = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.createDeal({
        project_name: 'New Project',
        client_name: '',
        client_budget: { value: 0, currency: 'CHF' },
        target_price: { value: 0, currency: 'CHF' },
        margin_target_pct: 30,
        fx_mode: 'live',
        stage: 'Demand',
      });
      const created = response.data;
      setDeal(created);
      setDeals((prev) => {
        const without = prev.filter((d) => d.id !== created.id);
        return [created, ...without];
      });
      setUndoStack([]);
      setRedoStack([]);
      setShowLoadDialog(false);
      setDealLibraryForecastFilter(null);
      setActiveTab('deals');
      setDealWorkspaceTab('deal');
      setMobileDealPane('line-items');
      toast.success('New deal created');
    } catch (error) {
      console.error('Failed to create deal:', error);
      toast.error('Failed to create deal');
    } finally {
      setLoading(false);
    }
  }, []);

  const buildDealPersistPayload = useCallback((currentDeal) => ({
    project_name: currentDeal.project_name,
    client_name: currentDeal.client_name,
    bexio_contact_id: currentDeal.bexio_contact_id ?? null,
    client_budget: currentDeal.client_budget,
    target_price: currentDeal.target_price,
    margin_target_pct: currentDeal.margin_target_pct,
    deal_discount_type: currentDeal.deal_discount_type || 'none',
    deal_discount_value: currentDeal.deal_discount_value ?? 0,
    core_currency: currentDeal.core_currency || 'CHF',
    fx_mode: currentDeal.fx_mode,
    stage: currentDeal.stage || 'Demand',
    deal_closure_date: currentDeal.deal_closure_date ?? null,
    service_rendered_date: currentDeal.service_rendered_date ?? null,
    link_group_id: currentDeal.link_group_id ?? null,
    payment_terms_days: (() => {
      const raw = parseInt(String(currentDeal.payment_terms_days ?? 30), 10);
      return Number.isFinite(raw) && raw >= 0 ? raw : 30;
    })(),
    signature_date: currentDeal.signature_date ?? null,
    upfront_amount_chf: currentDeal.upfront_amount_chf ?? null,
    upfront_percentage: currentDeal.upfront_percentage ?? 0,
    upfront_payment_delay_days: currentDeal.upfront_payment_delay_days ?? 0,
    upfront_payment_terms_days: currentDeal.upfront_payment_terms_days ?? 0,
    delivery_date: currentDeal.delivery_date ?? null,
    final_invoice_delay_days: currentDeal.final_invoice_delay_days ?? 0,
    final_payment_terms_days: currentDeal.final_payment_terms_days ?? 30,
    calendar_steps: Array.isArray(currentDeal.calendar_steps) ? currentDeal.calendar_steps : [],
  }), []);

  const persistCurrentDeal = useCallback(async ({
    showSuccessToast = true,
    failureMessage = null,
  } = {}) => {
    if (!dealRef.current) return;

    // Force commit any active inline edit (blur triggers commitChanges → updateLineItem)
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
      active.blur();
      // Wait for the blur-triggered API call to flush
      await new Promise(r => setTimeout(r, 500));
    }

    // Read the LATEST deal state from the ref (not the closure)
    const currentDeal = dealRef.current;
    if (!currentDeal) return null;

    setLoading(true);
    try {
      // Save metadata (backend recomputes + syncs active scenario)
      const response = await api.updateDeal(currentDeal.id, buildDealPersistPayload(currentDeal));
      setDeal(response.data);
      if (showSuccessToast) toast.success('Deal saved');
      return response.data;
    } catch (error) {
      console.error('Failed to save deal:', error);
      const fb = failureMessage
        || (showSuccessToast ? 'Failed to save deal' : 'Failed to prepare deal for Bexio issue');
      toast.error(apiErrorMessage(error, fb));
      return null;
    } finally {
      setLoading(false);
    }
  }, [buildDealPersistPayload]);

  // Save current deal explicitly
  const saveDeal = useCallback(async () => {
    await persistCurrentDeal({ showSuccessToast: true });
  }, [persistCurrentDeal]);

  const openIssueToBexioDialog = useCallback(() => {
    setIssueQuoteMode('line_items');
    setIssueQuoteBulkText('');
    setIssueQuoteBulkNote('');
    setPendingQuoteRequest(null);
    setIssueContactDialogOpen(false);
    setIssueContactForm((prev) => ({
      ...emptyBexioContactForm,
      contact_type: prev.contact_type || 'company',
      name_1: String(dealRef.current?.client_name || '').trim(),
    }));
    setIssueQuoteDialogOpen(true);
  }, []);

  const openDownloadQuotePdfDialog = useCallback(async () => {
    const current = dealRef.current;
    if (!current?.id) return;
    // Pre-select from backend SSOT (quote_pdf.default_vat_mode_for_currency).
    try {
      const res = await api.getDealQuotePdfOptions(current.id);
      const opts = res?.data || {};
      const mode = String(opts.default_vat_mode || '').toLowerCase();
      if (mode !== 'standard' && mode !== 'exempt') {
        throw new Error('Invalid quote VAT default from server');
      }
      setDownloadQuoteOptions(opts);
      setDownloadQuoteVatMode(mode);
      const lang = String(opts.default_language || 'fr').toLowerCase();
      setDownloadQuoteLang(lang === 'en' ? 'en' : 'fr');
      setDownloadQuoteDialogOpen(true);
    } catch (error) {
      console.error('Failed to load quote PDF options:', error);
      toast.error(apiErrorMessage(error, 'Could not load quote VAT defaults'));
    }
  }, []);

  const extractApiErrorDetail = useCallback(async (error, fallback = 'Request failed') => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            const formatted = formatApiErrorDetail(parsed?.detail);
            if (formatted) return formatted;
          } catch {
            if (text.trim()) return text.trim().slice(0, 300);
          }
        }
      } catch {
        // ignore parse failure and fall through
      }
    }
    return apiErrorMessage(error, fallback);
  }, []);

  const isMissingBexioContactDetail = useCallback(
    (detail) => typeof detail === 'string' && detail.includes('No bexio contact matches this CLIENT name.'),
    [],
  );

  const buildIssueQuotePayload = useCallback(() => {
    const mode = issueQuoteMode === 'bulk_total' ? 'bulk_total' : 'line_items';
    const payload = { mode };
    if (mode === 'bulk_total') {
      const titleText = issueQuoteBulkText.trim();
      if (titleText) payload.bulk_note = titleText;
      const bodyText = issueQuoteBulkNote.trim();
      if (!bodyText) {
        toast.error('Bulk mode requires body text');
        return null;
      }
      payload.bulk_line_text = bodyText;
    }
    return payload;
  }, [issueQuoteBulkNote, issueQuoteBulkText, issueQuoteMode]);

  const submitDownloadQuotePdf = useCallback(async () => {
    const current = dealRef.current;
    if (!current?.id) return;
    setDownloadQuoteBusy(true);
    try {
      const saved = await persistCurrentDeal({
        showSuccessToast: false,
        failureMessage: 'Failed to save deal before quote PDF',
      });
      if (!saved?.id) {
        // Persist already toasted; do not download stale server state.
        return;
      }
      const dealId = saved.id;
      const res = await api.downloadDealQuotePdf(dealId, {
        vatMode: downloadQuoteVatMode,
        lang: downloadQuoteLang,
        mode: 'line_items',
        disposition: 'attachment',
      });
      const blob = res?.data;
      if (!blob) throw new Error('Invalid PDF response');
      const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
      if (pdfBlob.type && pdfBlob.type.includes('json')) {
        throw new Error(await extractApiErrorDetail({ response: { data: pdfBlob } }, 'Could not download quote PDF'));
      }
      const headerName = String(res?.headers?.['content-disposition'] || '')
        .match(/filename="?([^";]+)"?/i)?.[1];
      const quoteRef = String(res?.headers?.['x-quote-ref'] || '').trim();
      const filename =
        headerName
        || (quoteRef ? `${quoteRef}.pdf` : '')
        || `quote-${String(dealId).slice(-8)}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setDownloadQuoteDialogOpen(false);
      const langLabel = downloadQuoteLang === 'en' ? 'EN' : 'FR';
      toast.success(
        downloadQuoteVatMode === 'exempt'
          ? `Quote PDF downloaded (${langLabel}, VAT exempt)`
          : `Quote PDF downloaded (${langLabel}, VAT 8.1%)`,
      );
    } catch (error) {
      console.error('Failed to download quote PDF:', error);
      const detail = await extractApiErrorDetail(error, 'Could not download quote PDF');
      toast.error(detail);
    } finally {
      setDownloadQuoteBusy(false);
    }
  }, [downloadQuoteLang, downloadQuoteVatMode, extractApiErrorDetail, persistCurrentDeal]);

  const triggerQuotePdfDownload = useCallback(async (dealId, quoteId, documentNr = '') => {
    try {
      const res = await api.downloadBexioQuotePdf(dealId, quoteId, { disposition: 'attachment' });
      const blob = res?.data;
      if (!blob) throw new Error('Invalid PDF response');
      const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
      const filenameRoot = String(documentNr || `quote-${quoteId}`)
        .replace(/[^A-Za-z0-9._-]+/g, '_')
        .replace(/^_+|_+$/g, '') || `quote-${quoteId}`;
      const url = URL.createObjectURL(pdfBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filenameRoot}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      throw error;
    }
  }, []);

  const openQuotePdfInNewTab = useCallback(async (dealId, quoteId) => {
    const previewWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!previewWindow) throw new Error('Popup blocked');
    try {
      const res = await api.downloadBexioQuotePdf(dealId, quoteId, { disposition: 'inline' });
      const blob = res?.data;
      if (!blob) throw new Error('Invalid PDF response');
      const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      previewWindow.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      throw error;
    }
  }, []);

  const finalizeIssuedQuote = useCallback(async (dealId, data, mode) => {
    const docNr = String(data?.document_nr || '').trim();
    const quoteId = data?.quote_id;
    if (quoteId != null) {
      try {
        await triggerQuotePdfDownload(dealId, quoteId, docNr);
      } catch (pdfError) {
        console.error('Quote created but PDF download failed:', pdfError);
        const detail = await extractApiErrorDetail(pdfError, 'Quote created but PDF download failed');
        toast.error(detail);
      }
    }
    setLastIssuedQuote({
      dealId,
      quoteId: quoteId ?? null,
      documentNr: docNr,
      mode: data?.mode || mode,
    });
    setIssueContactDialogOpen(false);
    setPendingQuoteRequest(null);
    setIssueQuoteDialogOpen(false);
    toast.success(docNr ? `Quote ${docNr} created in Bexio` : 'Quote created in Bexio');
  }, [extractApiErrorDetail, triggerQuotePdfDownload]);

  const submitIssueToBexio = useCallback(async () => {
    const savedDeal = await persistCurrentDeal({ showSuccessToast: false });
    if (!savedDeal?.id) return;
    const payload = buildIssueQuotePayload();
    if (!payload) return;
    const mode = payload.mode;
    setIssueQuoteSubmitting(true);
    try {
      const response = await api.issueBexioQuote(savedDeal.id, payload);
      await finalizeIssuedQuote(savedDeal.id, response.data || {}, mode);
    } catch (error) {
      const detail = await extractApiErrorDetail(error, 'Failed to issue quote to Bexio');
      console.error('Failed to issue quote to Bexio:', error);
      if (isMissingBexioContactDetail(detail)) {
        setPendingQuoteRequest({ dealId: savedDeal.id, payload });
        setIssueContactForm({
          ...emptyBexioContactForm,
          contact_type: 'company',
          name_1: String(savedDeal.client_name || '').trim(),
        });
        setIssueContactDialogOpen(true);
        return;
      }
      toast.error(detail || 'Failed to issue quote to Bexio');
    } finally {
      setIssueQuoteSubmitting(false);
    }
  }, [buildIssueQuotePayload, extractApiErrorDetail, finalizeIssuedQuote, isMissingBexioContactDetail, persistCurrentDeal]);

  const submitCreateBexioContact = useCallback(async () => {
    if (!pendingQuoteRequest?.dealId || !pendingQuoteRequest?.payload) return;
    const name1 = String(issueContactForm.name_1 || '').trim();
    if (!name1) {
      toast.error('Contact name is required');
      return;
    }
    setIssueContactSubmitting(true);
    try {
      const createRes = await api.createBexioDealContact(pendingQuoteRequest.dealId, {
        ...issueContactForm,
        name_1: name1,
      });
      const created = createRes.data || {};
      if (created?.deal) {
        setDeal(created.deal);
      }
      toast.success(created?.label ? `Contact created: ${created.label}` : 'Contact created in Bexio');
      const retryPayload = {
        ...pendingQuoteRequest.payload,
        contact_id_override: created.contact_id,
      };
      const quoteRes = await api.issueBexioQuote(pendingQuoteRequest.dealId, retryPayload);
      await finalizeIssuedQuote(pendingQuoteRequest.dealId, quoteRes.data || {}, pendingQuoteRequest.payload.mode || 'line_items');
    } catch (error) {
      console.error('Failed to create Bexio contact:', error);
      const detail = await extractApiErrorDetail(error, 'Failed to create Bexio contact');
      toast.error(detail);
    } finally {
      setIssueContactSubmitting(false);
    }
  }, [extractApiErrorDetail, finalizeIssuedQuote, issueContactForm, pendingQuoteRequest]);

  const handleDownloadLastIssuedQuote = useCallback(async () => {
    if (!lastIssuedQuote?.dealId || !lastIssuedQuote?.quoteId) return;
    setIssueQuotePdfBusy(true);
    try {
      await triggerQuotePdfDownload(lastIssuedQuote.dealId, lastIssuedQuote.quoteId, lastIssuedQuote.documentNr);
    } catch (error) {
      console.error('Failed to download last issued quote PDF:', error);
      const detail = await extractApiErrorDetail(error, 'Could not download quote PDF');
      toast.error(detail);
    } finally {
      setIssueQuotePdfBusy(false);
    }
  }, [extractApiErrorDetail, lastIssuedQuote, triggerQuotePdfDownload]);

  const handleOpenLastIssuedQuote = useCallback(async () => {
    if (!lastIssuedQuote?.dealId || !lastIssuedQuote?.quoteId) return;
    setIssueQuotePdfBusy(true);
    try {
      await openQuotePdfInNewTab(lastIssuedQuote.dealId, lastIssuedQuote.quoteId);
    } catch (error) {
      console.error('Failed to open last issued quote PDF:', error);
      const detail = await extractApiErrorDetail(error, 'Could not open quote PDF');
      toast.error(detail);
    } finally {
      setIssueQuotePdfBusy(false);
    }
  }, [extractApiErrorDetail, lastIssuedQuote, openQuotePdfInNewTab]);

  // Update deal metadata
  const updateDeal = useCallback(async (data) => {
    if (!deal) return;
    setLoading(true);
    try {
      const payload = {
        project_name: deal.project_name,
        client_name: deal.client_name,
        bexio_contact_id: deal.bexio_contact_id ?? null,
        client_budget: deal.client_budget,
        target_price: deal.target_price,
        margin_target_pct: deal.margin_target_pct,
        deal_discount_type: deal.deal_discount_type || 'none',
        deal_discount_value: deal.deal_discount_value ?? 0,
        core_currency: deal.core_currency || 'CHF',
        fx_mode: deal.fx_mode || 'live',
        stage: deal.stage || 'Demand',
        deal_closure_date: deal.deal_closure_date ?? null,
        service_rendered_date: deal.service_rendered_date ?? null,
        link_group_id: deal.link_group_id ?? null,
        payment_terms_days: deal.payment_terms_days ?? 30,
        signature_date: deal.signature_date ?? null,
        upfront_amount_chf: deal.upfront_amount_chf ?? null,
        upfront_percentage: deal.upfront_percentage ?? 0,
        upfront_payment_delay_days: deal.upfront_payment_delay_days ?? 0,
        upfront_payment_terms_days: deal.upfront_payment_terms_days ?? 0,
        delivery_date: deal.delivery_date ?? null,
        final_invoice_delay_days: deal.final_invoice_delay_days ?? 0,
        final_payment_terms_days: deal.final_payment_terms_days ?? 30,
        calendar_steps: Array.isArray(deal.calendar_steps) ? deal.calendar_steps : [],
        ...data,
      };
      const response = await api.updateDeal(deal.id, payload);
      setDealWithUndo(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to update deal:', error);
      toast.error(apiErrorMessage(error, 'Failed to update deal'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [deal, setDealWithUndo]);

  const handleMarkDealQuoted = useCallback(async () => {
    if (!deal || deal.stage === 'Quoted') return;
    setIssueQuoteStageBusy(true);
    try {
      await updateDeal({ stage: 'Quoted' });
      toast.success('Deal stage updated to Quote Issued');
    } catch (error) {
      console.error('Failed to update deal stage after quote issue:', error);
    } finally {
      setIssueQuoteStageBusy(false);
    }
  }, [deal, updateDeal]);

  // Delete deal
  const deleteDeal = useCallback(async (dealId) => {
    setLoading(true);
    try {
      await api.deleteDeal(dealId);
      await fetchDeals();
      if (deal?.id === dealId) {
        setDeal(null);
        setUndoStack([]);
        setRedoStack([]);
      }
      toast.success('Deal deleted');
    } catch (error) {
      console.error('Failed to delete deal:', error);
      toast.error('Failed to delete deal');
    } finally {
      setLoading(false);
    }
  }, [deal, fetchDeals]);

  // Toggle FX lock
  const toggleFXLock = useCallback(async () => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = deal.fx_mode === 'locked'
        ? await api.unlockFX(deal.id)
        : await api.lockFX(deal.id);
      setDealWithUndo(response.data);
      toast.success(response.data.fx_mode === 'locked' ? 'FX rates locked' : 'Using live FX rates');
    } catch (error) {
      console.error('Failed to toggle FX lock:', error);
      toast.error('Failed to toggle FX lock');
    } finally {
      setLoading(false);
    }
  }, [deal, setDealWithUndo]);

  // Line item operations
  const addLineItem = useCallback(async () => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.addLineItem(deal.id, {
        title: 'Untitled',
        name: 'Untitled',
        description_plain: '',
        description_html: '',
        category: '',
        qty: 1,
        revenue_unit: 0,
        revenue_currency: deal.core_currency || 'CHF',
        discount_type: 'none',
        discount_value: 0,
        cost_unit: 0,
        cost_currency: deal.core_currency || 'CHF',
      });
      setDealWithUndo(response.data);
    } catch (error) {
      console.error('Failed to add line item:', error);
      toast.error('Failed to add line item');
    } finally {
      setLoading(false);
    }
  }, [deal, setDealWithUndo]);

  const updateLineItem = useCallback(async (itemId, data) => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.updateLineItem(deal.id, itemId, data);
      setDealWithUndo(response.data);
    } catch (error) {
      console.error('Failed to update line item:', error);
      toast.error('Failed to update line item');
    } finally {
      setLoading(false);
    }
  }, [deal, setDealWithUndo]);

  const deleteLineItem = useCallback(async (itemId) => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.deleteLineItem(deal.id, itemId);
      setDealWithUndo(response.data);
    } catch (error) {
      console.error('Failed to delete line item:', error);
      toast.error('Failed to delete line item');
    } finally {
      setLoading(false);
    }
  }, [deal, setDealWithUndo]);

  const duplicateLineItem = useCallback(async (itemId) => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.duplicateLineItem(deal.id, itemId);
      setDealWithUndo(response.data);
    } catch (error) {
      console.error('Failed to duplicate line item:', error);
      toast.error('Failed to duplicate line item');
    } finally {
      setLoading(false);
    }
  }, [deal, setDealWithUndo]);

  // Create category
  const createCategory = useCallback(async (name) => {
    try {
      const response = await api.createCategory(name);
      const newCat = response.data;
      setCategories(prev => {
        // Check if already exists
        if (prev.some(c => c.id === newCat.id)) return prev;
        return [...prev, newCat];
      });
      toast.success(`Category "${name}" created`);
      return newCat;
    } catch (error) {
      console.error('Failed to create category:', error);
      toast.error('Failed to create category');
      return null;
    }
  }, []);

  const deleteCategory = useCallback(async (category) => {
    if (!category?.id) return false;
    try {
      await api.deleteCategory(category.id);
      setCategories((prev) => prev.filter((c) => c.id !== category.id));
      toast.success(`Category "${category.name}" removed`);
      return true;
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error(error?.response?.data?.detail || 'Failed to delete category');
      return false;
    }
  }, []);

  // Create vendor
  const createVendor = useCallback(async (name) => {
    try {
      const response = await api.createVendor(name);
      const newVendor = response.data;
      setVendors(prev => {
        if (prev.some(v => v.id === newVendor.id)) return prev;
        return [...prev, newVendor];
      });
      toast.success(`Vendor "${name}" created`);
      return newVendor;
    } catch (error) {
      console.error('Failed to create vendor:', error);
      toast.error('Failed to create vendor');
      return null;
    }
  }, []);

  const handleForecastBucketDrill = useCallback(async ({ view, bucket, filters = {} }) => {
    try {
      const res = await api.getForecastBucketDeals(view, bucket, filters);
      setDealLibraryForecastFilter({
        view,
        bucket,
        filters,
        deals: res.data.deals,
        banner: forecastBucketBanner(view, bucket),
      });
      setShowLoadDialog(true);
    } catch (error) {
      console.error('Failed to load forecast bucket deals:', error);
      toast.error('Could not load deals for this period');
    }
  }, []);

  const refreshForecastLibraryDeals = useCallback(async () => {
    const f = forecastFilterRef.current;
    if (!f?.view) return;
    try {
      const res = await api.getForecastBucketDeals(f.view, f.bucket, f.filters || {});
      setDealLibraryForecastFilter((prev) =>
        prev && prev.view === f.view && prev.bucket === f.bucket
          ? { ...prev, deals: res.data.deals }
          : prev,
      );
    } catch (error) {
      console.error('Failed to refresh filtered deals:', error);
    }
  }, []);

  const deleteDealFromLibrary = useCallback(
    async (dealId) => {
      await deleteDeal(dealId);
      await refreshForecastLibraryDeals();
    },
    [deleteDeal, refreshForecastLibraryDeals],
  );

  const patchDealFromLibrary = useCallback(async (dealId, patch) => {
    const response = await api.patchDeal(dealId, patch);
    const updated = response.data;
    setDeals((prev) => prev.map((d) => (d.id === dealId ? updated : d)));
    setDealLibraryForecastFilter((prev) => {
      if (!prev?.deals) return prev;
      return { ...prev, deals: prev.deals.map((d) => (d.id === dealId ? updated : d)) };
    });
    return updated;
  }, []);

  const bulkLinkDealsFromLibrary = useCallback(async (dealIds, opts = {}) => {
    const response = await api.bulkLinkDeals(dealIds, opts);
    const updated = response.data;
    if (!Array.isArray(updated)) {
      throw new Error('Unexpected response from bulk-link (expected an array of deals).');
    }
    const byId = new Map(updated.map((d) => [d.id, d]));
    setDeals((prev) => prev.map((d) => byId.get(d.id) ?? d));
    setDealLibraryForecastFilter((prev) => {
      if (!prev?.deals) return prev;
      return { ...prev, deals: prev.deals.map((d) => byId.get(d.id) ?? d) };
    });
    return updated;
  }, []);

  const createQuickDealFromLibrary = useCallback(async (payload) => {
    const response = await api.createQuickDeal(payload);
    const created = response.data;
    setDealLibraryForecastFilter(null);
    setDeals((prev) => [created, ...prev]);
    toast.success('Quick deal created');
    return created;
  }, []);

  // Duplicate deal
  const handleDuplicateDeal = useCallback(async (dealId) => {
    setLoading(true);
    try {
      const response = await api.duplicateDeal(dealId);
      const newDeal = response.data;
      setDeal(newDeal);
      setUndoStack([]);
      setRedoStack([]);
      setDealLibraryForecastFilter(null);
      setShowLoadDialog(false);
      // Refresh categories and vendors
      const [catRes, venRes] = await Promise.all([api.getCategories(), api.getVendors()]);
      setCategories(catRes.data);
      setVendors(venRes.data);
      toast.success(`Deal duplicated as "${newDeal.project_name}"`);
    } catch (error) {
      console.error('Failed to duplicate deal:', error);
      toast.error(error.response?.data?.detail || 'Failed to duplicate deal');
    } finally {
      setLoading(false);
    }
  }, []);

  // Bulk import (Paste Assistant)
  const handleBulkImport = useCallback(async (mode, items, startIndex) => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.bulkAddLineItems(deal.id, { mode, items, start_index: startIndex });
      setDealWithUndo(response.data);
      // Refresh categories and vendors (may have been auto-created)
      const [catRes, venRes] = await Promise.all([api.getCategories(), api.getVendors()]);
      setCategories(catRes.data);
      setVendors(venRes.data);
      toast.success(`${items.length} row${items.length !== 1 ? 's' : ''} ${mode === 'fill' ? 'updated' : 'imported'}`);
    } catch (error) {
      console.error('Failed to import rows:', error);
      toast.error(error.response?.data?.detail || 'Failed to import');
    } finally {
      setLoading(false);
    }
  }, [deal, setDealWithUndo]);

  // Bulk Auto Pricing (header-level: overwrites all rows where source > 0)
  const handleBulkAutoPrice = useCallback(async (direction, opts = {}) => {
    if (!deal) return;
    const fxSnapshot = deal.fx_mode === 'locked' ? deal.fx_snapshot : fxRates;
    const margin = deal.margin_target_pct;

    if (!margin || isNaN(margin) || margin >= 100 || margin < 0) {
      toast.warning('Auto pricing unavailable: invalid margin target');
      return;
    }

    const items = deal.line_items.map(li => {
      const base = deal.core_currency || 'CHF';
      if (direction === 'revenue') {
        if (li.cost_unit <= 0) return {};
        const costBase = toBase(li.cost_unit, li.cost_currency, base, fxSnapshot);
        const revBase = costBase / (1 - margin / 100);
        const revInCur = fromBase(revBase, base, li.revenue_currency, fxSnapshot);
        return { revenue_unit: Math.round(revInCur * 100) / 100 };
      } else {
        if (li.revenue_unit <= 0) return {};
        const revBase = toBase(li.revenue_unit, li.revenue_currency, base, fxSnapshot);
        const costBase = revBase * (1 - margin / 100);
        const costInCur = fromBase(costBase, base, li.cost_currency, fxSnapshot);
        return { cost_unit: Math.round(costInCur * 100) / 100 };
      }
    });

    const eligible = items.filter(it => Object.keys(it).length > 0).length;
    if (eligible === 0) {
      toast.info(`No eligible rows — ${direction === 'revenue' ? 'cost' : 'revenue'} is zero on all rows`);
      return;
    }

    const fieldLabel = direction === 'revenue' ? 'Revenue/Unit' : 'Cost/Unit';
    if (!opts.skipConfirm) {
      if (!window.confirm(`This will overwrite ${fieldLabel} on ${eligible} row${eligible !== 1 ? 's' : ''} using ${margin}% margin target. Continue?`)) {
        return;
      }
    }

    setLoading(true);
    try {
      const response = await api.bulkAddLineItems(deal.id, { mode: 'fill', items, start_index: 0 });
      setDealWithUndo(response.data);
      toast.success(`Auto-${direction}: ${eligible} row${eligible !== 1 ? 's' : ''} updated`);
    } catch (error) {
      console.error('Bulk auto price failed:', error);
      toast.error('Auto pricing failed');
    } finally {
      setLoading(false);
    }
  }, [deal, fxRates, setDealWithUndo]);

  const runDealAdvisor = useCallback(async () => {
    if (!deal) return;
    setAdvisorLoading(true);
    setAdvisorError(null);
    try {
      const response = await api.analyzeDealAdvisor(deal.id, advisorFocus);
      setAdvisorResult(response.data);
    } catch (error) {
      console.error('Deal advisor failed:', error);
      setAdvisorResult(null);
      const detail = error.response?.data?.detail;
      setAdvisorError(
        typeof detail === 'string' ? detail : error.message || 'Analysis failed'
      );
    } finally {
      setAdvisorLoading(false);
    }
  }, [deal, advisorFocus]);

  const handleAdvisorApplyCta = useCallback(
    async (cta, key) => {
      if (!deal) return;
      const action = cta?.action;
      if (!action || !action.type) return;
      setAdvisorApplyingKey(key);
      try {
        if (action.type === 'update_line_item') {
          await updateLineItem(action.line_item_id, action.patch);
          toast.success('Line updated');
        } else if (action.type === 'update_deal') {
          await updateDeal(action.patch);
          toast.success('Deal updated');
        } else if (action.type === 'bulk_auto_price') {
          await handleBulkAutoPrice(action.direction, { skipConfirm: true });
        }
        setAdvisorResult(null);
        toast.info('Re-run analysis for updated suggestions');
      } catch (error) {
        console.error('Advisor CTA failed:', error);
        toast.error(error.response?.data?.detail || 'Action failed');
      } finally {
        setAdvisorApplyingKey(null);
      }
    },
    [deal, updateLineItem, updateDeal, handleBulkAutoPrice]
  );

  // ─── Scenario Operations ─────────────────────────────
  const handleScenarioCreate = useCallback(async (name) => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.createScenario(deal.id, { name });
      setDeal(response.data);
      setUndoStack([]);
      setRedoStack([]);
      toast.success(`Scenario "${name}" created`);
    } catch (error) {
      console.error('Failed to create scenario:', error);
      toast.error('Failed to create scenario');
    } finally {
      setLoading(false);
    }
  }, [deal]);

  const handleScenarioDuplicate = useCallback(async (scenarioId, name) => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.duplicateScenario(deal.id, scenarioId, { name });
      setDeal(response.data);
      setUndoStack([]);
      setRedoStack([]);
      toast.success(`Scenario duplicated as "${name}"`);
    } catch (error) {
      console.error('Failed to duplicate scenario:', error);
      toast.error('Failed to duplicate scenario');
    } finally {
      setLoading(false);
    }
  }, [deal]);

  const handleScenarioActivate = useCallback(async (scenarioId) => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.activateScenario(deal.id, scenarioId);
      setDeal(response.data);
      setUndoStack([]);
      setRedoStack([]);
      const scenario = response.data.scenarios?.find(s => s.id === scenarioId);
      toast.success(`Switched to "${scenario?.name || 'scenario'}"`);
    } catch (error) {
      console.error('Failed to switch scenario:', error);
      toast.error('Failed to switch scenario');
    } finally {
      setLoading(false);
    }
  }, [deal]);

  const handleScenarioDelete = useCallback(async (scenarioId) => {
    if (!deal) return;
    setLoading(true);
    try {
      const response = await api.deleteScenario(deal.id, scenarioId);
      setDeal(response.data);
      setUndoStack([]);
      setRedoStack([]);
      toast.success('Scenario deleted');
    } catch (error) {
      console.error('Failed to delete scenario:', error);
      toast.error('Failed to delete scenario');
    } finally {
      setLoading(false);
    }
  }, [deal]);

  const handleScenarioRename = useCallback(async (scenarioId, name) => {
    if (!deal) return;
    try {
      const response = await api.renameScenario(deal.id, scenarioId, { name });
      setDeal(response.data);
      toast.success(`Scenario renamed to "${name}"`);
    } catch (error) {
      console.error('Failed to rename scenario:', error);
      toast.error('Failed to rename scenario');
    }
  }, [deal]);

  const handleAdvisorOpenChange = useCallback((open) => {
    setAdvisorOpen(open);
    if (!open) {
      setAdvisorResult(null);
      setAdvisorError(null);
    }
  }, []);

  const addDealNote = useCallback(async (comment) => {
    if (!dealRef.current) return null;
    try {
      const response = await api.addDealNote(dealRef.current.id, { comment });
      setDeal(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to add deal note:', error);
      toast.error(error.response?.data?.detail || 'Failed to save note');
      throw error;
    }
  }, []);

  const handleMainTabChange = useCallback(async (tab) => {
    if (tab !== 'maintenance') {
      setMaintenanceFocusItemId(null);
    }
    if (tab === 'deals') {
      setActiveTab('deals');
      setDealLibraryForecastFilter(null);
      setDealWorkspaceTab('deal');
      setMobileDealPane('line-items');
      if (dealRef.current && !showLoadDialog) {
        await saveDeal();
      }
      setShowLoadDialog(true);
      void fetchDeals();
      return;
    }

    setActiveTab(tab);
    setDealLibraryForecastFilter(null);
    setShowLoadDialog(false);
  }, [fetchDeals, saveDeal, showLoadDialog]);

  const closeDealWorkspace = useCallback(() => {
    setDealWorkspaceTab('deal');
    setMobileDealPane('line-items');
    setDealLibraryForecastFilter(null);
    setShowLoadDialog(true);
    void fetchDeals();
  }, [fetchDeals]);

  const handleForecastOpenDeal = useCallback((dealId) => {
    const id = String(dealId || '').trim();
    if (id.startsWith('maintenance:')) {
      setDealLibraryForecastFilter(null);
      setShowLoadDialog(false);
      setActiveTab('maintenance');
      return;
    }
    void loadDeal(id);
  }, [loadDeal]);

  const handleOpenMaintenanceFromNotification = useCallback((itemId) => {
    const mid = String(itemId || '').trim();
    if (!mid) return;
    setDealLibraryForecastFilter(null);
    setShowLoadDialog(false);
    setActiveTab('maintenance');
    setMaintenanceFocusItemId(mid);
  }, []);

  const handleNotificationAction = useCallback((action, notification) => {
    if (action?.kind === 'maintenance') {
      handleOpenMaintenanceFromNotification(action.id);
      return;
    }
    if (action?.kind === 'url' && typeof window !== 'undefined') {
      window.open(String(action.url || ''), '_blank', 'noopener,noreferrer');
      return;
    }
    // Fallback for notification rows without action_url but with maintenance source mapping.
    const sourceType = String(notification?.source_type || '').trim();
    const sourceId = String(notification?.source_id || '').trim();
    if (sourceType === 'maintenance' && sourceId) {
      handleOpenMaintenanceFromNotification(sourceId);
    }
  }, [handleOpenMaintenanceFromNotification]);

  const handleStageChange = useCallback((newStage) => {
    if (!deal) return;
    if (newStage === deal.stage) return;

    if (newStage === 'Rejected') {
      setRejectionComment('');
      setRejectDialogOpen(true);
      return;
    }

    updateDeal({ stage: newStage }).catch(() => {});
  }, [deal, updateDeal]);

  const submitRejectedStage = useCallback(async () => {
    if (!deal) return;
    const comment = rejectionComment.trim();
    if (!comment) {
      toast.error('Please add a rejection reason before saving');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        project_name: deal.project_name,
        client_name: deal.client_name,
        bexio_contact_id: deal.bexio_contact_id ?? null,
        client_budget: deal.client_budget,
        target_price: deal.target_price,
        margin_target_pct: deal.margin_target_pct,
        core_currency: deal.core_currency || 'CHF',
        fx_mode: deal.fx_mode || 'live',
        stage: 'Rejected',
        rejection_comment: comment,
        deal_closure_date: deal.deal_closure_date ?? null,
        service_rendered_date: deal.service_rendered_date ?? null,
        link_group_id: deal.link_group_id ?? null,
        payment_terms_days: deal.payment_terms_days ?? 30,
        signature_date: deal.signature_date ?? null,
        upfront_amount_chf: deal.upfront_amount_chf ?? null,
        upfront_percentage: deal.upfront_percentage ?? 0,
        upfront_payment_delay_days: deal.upfront_payment_delay_days ?? 0,
        upfront_payment_terms_days: deal.upfront_payment_terms_days ?? 0,
        delivery_date: deal.delivery_date ?? null,
        final_invoice_delay_days: deal.final_invoice_delay_days ?? 0,
        final_payment_terms_days: deal.final_payment_terms_days ?? 30,
      };
      const response = await api.updateDeal(deal.id, payload);
      setDealWithUndo(response.data);
      setRejectDialogOpen(false);
      setRejectionComment('');
      setDealWorkspaceTab('notes');
    } catch (error) {
      console.error('Failed to update deal stage:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as Rejected');
    } finally {
      setLoading(false);
    }
  }, [deal, rejectionComment, setDealWithUndo]);

  return (
    <div className="flex h-dvh min-h-0 w-full max-w-[100vw] flex-col overflow-hidden bg-[var(--color-background)] text-[var(--color-text-primary)]">
      <TopBar
        fxRates={deal?.fx_mode === 'locked' ? deal.fx_snapshot : fxRates}
        fxMode={deal?.fx_mode || 'live'}
        onLockToggle={toggleFXLock}
        onSave={saveDeal}
        onNew={createNewDeal}
        loading={loading}
        activeTab={activeTab}
        onTabChange={handleMainTabChange}
        presentationCurrency={presentationCurrency}
        onPresentationCurrencyChange={setPresentationCurrency}
        vendorCurrency={vendorCurrency}
        onVendorCurrencyChange={setVendorCurrency}
        hasDeal={!!deal}
        onPaste={() => setShowPasteDialog(true)}
        stage={deal?.stage || 'Demand'}
        onStageChange={handleStageChange}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={undoStack.length > 0}
        canRedo={redoStack.length > 0}
        scenarios={deal?.scenarios || []}
        activeScenarioId={deal?.active_scenario_id}
        onScenarioActivate={handleScenarioActivate}
        onScenarioCreate={handleScenarioCreate}
        onScenarioDuplicate={handleScenarioDuplicate}
        onScenarioDelete={handleScenarioDelete}
        onScenarioRename={handleScenarioRename}
        dealWorkspaceTab={dealWorkspaceTab}
        onDealWorkspaceTabChange={setDealWorkspaceTab}
        onCloseDealWorkspace={closeDealWorkspace}
        onIssueToBexio={openIssueToBexioDialog}
        issueToBexioDisabled={
          !deal?.id
          || !String(deal?.client_name || '').trim()
          || !['CHF', 'EUR', 'USD'].includes(String(deal?.core_currency || 'CHF').toUpperCase())
          || issueQuoteSubmitting
        }
        onDownloadQuotePdf={openDownloadQuotePdfDialog}
        downloadQuotePdfDisabled={
          !deal?.id
          || !['CHF', 'EUR', 'USD'].includes(String(deal?.core_currency || 'CHF').toUpperCase())
          || downloadQuoteBusy
        }
        showDealContext={activeTab === 'deals' && !!deal && !showLoadDialog}
        onNotificationAction={handleNotificationAction}
      />

      {activeTab === 'deals' && !!deal && !showLoadDialog && lastIssuedQuote?.dealId === deal.id && (
        <div className="border-b border-emerald-100 bg-emerald-50 px-3 py-2">
          <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-2 text-xs text-emerald-900">
            <span className="font-semibold">
              Quote issued{lastIssuedQuote.documentNr ? `: ${lastIssuedQuote.documentNr}` : ''}
            </span>
            <span className="rounded border border-emerald-200 bg-white px-1.5 py-0.5 text-[11px]">
              {lastIssuedQuote.mode === 'bulk_total' ? 'Bulk total' : 'Line-by-line'}
            </span>
            <button
              type="button"
              onClick={handleOpenLastIssuedQuote}
              disabled={issueQuotePdfBusy || !lastIssuedQuote.quoteId}
              className="rounded border border-emerald-300 bg-white px-2 py-1 text-[11px] font-medium disabled:opacity-50"
            >
              Open PDF
            </button>
            <button
              type="button"
              onClick={handleDownloadLastIssuedQuote}
              disabled={issueQuotePdfBusy || !lastIssuedQuote.quoteId}
              className="rounded border border-emerald-300 bg-white px-2 py-1 text-[11px] font-medium disabled:opacity-50"
            >
              Download again
            </button>
            {deal?.stage !== 'Quoted' && (
              <button
                type="button"
                onClick={handleMarkDealQuoted}
                disabled={issueQuoteStageBusy}
                className="rounded border border-emerald-300 bg-white px-2 py-1 text-[11px] font-medium disabled:opacity-50"
              >
                {issueQuoteStageBusy ? 'Updating stage…' : 'Change Stage to Quote Issued'}
              </button>
            )}
            <button
              type="button"
              onClick={() => setLastIssuedQuote(null)}
              className="ml-auto rounded border border-emerald-200 bg-transparent px-2 py-1 text-[11px]"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {showLoadDialog ? (
        <DealLibraryPage
          deals={dealLibraryForecastFilter?.deals ?? deals}
          contextBanner={dealLibraryForecastFilter?.banner ?? null}
          onClose={() => {
            setDealLibraryForecastFilter(null);
            if (!deal && activeTab === 'deals') {
              setActiveTab('forecast');
            }
            setShowLoadDialog(false);
          }}
          onSelect={loadDeal}
          onDelete={dealLibraryForecastFilter ? deleteDealFromLibrary : deleteDeal}
          onDuplicate={handleDuplicateDeal}
          onPatchDeal={patchDealFromLibrary}
          onBulkLinkDeals={bulkLinkDealsFromLibrary}
          onCreateQuickDeal={createQuickDealFromLibrary}
          loading={loading}
        />
      ) : activeTab === 'deals' ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {dealWorkspaceTab === 'invoices' ? (
            <DealInvoicesPaymentsTab deal={deal} loading={loading} onDealReload={reloadCurrentDeal} />
          ) : dealWorkspaceTab === 'vendor-payments' ? (
            <DealVendorPaymentsTab deal={deal} onDealReload={reloadCurrentDeal} />
          ) : dealWorkspaceTab === 'calendar' ? (
            <DealCalendarTab
              deal={deal}
              loading={loading}
              onUpdateCalendarSteps={(calendar_steps) => updateDeal({ calendar_steps })}
            />
          ) : dealWorkspaceTab === 'notes' ? (
            <DealNotesTab deal={deal} onAddNote={addDealNote} />
          ) : dealWorkspaceTab === 'master-price-list' ? (
            <MasterPriceListTab
              dealId={deal?.id}
              categories={categories}
              vendors={vendors}
              fxRates={fxRates}
              onCreateCategory={createCategory}
              onDeleteCategory={deleteCategory}
              onCreateVendor={createVendor}
              onImported={(updatedDeal) => {
                setDealWithUndo(updatedDeal);
                setDealWorkspaceTab('deal');
              }}
            />
          ) : (
            <>
              <div className="border-b border-gray-200 bg-white px-3 py-1.5 lg:hidden">
                <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
                  <button
                    type="button"
                    className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-medium ${mobileDealPane === 'details' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setMobileDealPane('details')}
                  >
                    Details
                  </button>
                  <button
                    type="button"
                    className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-medium ${mobileDealPane === 'line-items' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setMobileDealPane('line-items')}
                  >
                    Line items
                  </button>
                  <button
                    type="button"
                    className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-medium ${mobileDealPane === 'decision' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setMobileDealPane('decision')}
                  >
                    Decision
                  </button>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden lg:flex-row lg:overflow-hidden">
                <div className={`${mobileDealPane === 'details' ? 'flex' : 'hidden'} min-h-0 min-w-0 flex-1 flex-col lg:flex lg:w-[280px] lg:flex-none`}>
                  <LeftPanel
                    deal={deal}
                    onUpdate={updateDeal}
                    loading={loading}
                    presentationCurrency={presentationCurrency}
                    fxRates={deal?.fx_mode === 'locked' ? deal.fx_snapshot : fxRates}
                  />
                </div>

                <div className={`${mobileDealPane === 'line-items' ? 'flex' : 'hidden'} min-h-0 w-full min-w-0 flex-1 flex-col lg:flex lg:min-h-0`}>
                  <LineItemTable
                    items={deal?.line_items || []}
                    categories={categories}
                    vendors={vendors}
                    onAddItem={addLineItem}
                    onUpdateItem={updateLineItem}
                    onDeleteItem={deleteLineItem}
                    onDuplicateItem={duplicateLineItem}
                    onCreateCategory={createCategory}
                    onDeleteCategory={deleteCategory}
                    onCreateVendor={createVendor}
                    loading={loading}
                    marginTarget={deal?.margin_target_pct || 30}
                    presentationCurrency={presentationCurrency}
                    vendorCurrency={vendorCurrency}
                    coreCurrency={deal?.core_currency || 'CHF'}
                    fxRates={deal?.fx_mode === 'locked' ? deal.fx_snapshot : fxRates}
                    dealTotals={deal ? {
                      total_revenue_chf: deal.total_revenue_chf,
                      total_cost_chf: deal.total_cost_chf,
                      total_margin_chf: deal.total_margin_chf,
                      total_margin_pct: deal.total_margin_pct,
                    } : null}
                    columnConfig={columnConfig}
                    onImportFromMasterPriceList={() => setShowImportMasterDialog(true)}
                  />
                </div>

                <div className={`${mobileDealPane === 'decision' ? 'flex' : 'hidden'} min-h-0 min-w-0 flex-1 flex-col lg:flex lg:flex-none ${rightPanelCollapsed ? 'lg:w-[68px]' : 'lg:w-[250px]'}`}>
                  <RightPanel
                    deal={deal}
                    collapsed={rightPanelCollapsed}
                    onCollapsedChange={setRightPanelCollapsed}
                    advisorOpen={advisorOpen}
                    onAdvisorOpenChange={handleAdvisorOpenChange}
                    advisorFocus={advisorFocus}
                    onAdvisorFocusChange={setAdvisorFocus}
                    advisorResult={advisorResult}
                    advisorLoading={advisorLoading}
                    advisorError={advisorError}
                    advisorApplyingKey={advisorApplyingKey}
                    onAdvisorAnalyze={runDealAdvisor}
                    onAdvisorApplyCta={handleAdvisorApplyCta}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      ) : activeTab === 'vendors' ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <VendorTab
            onOpenDeal={(dealId) => loadDeal(dealId)}
            onOpenDealVendorPayments={(dealId) => loadDeal(dealId, { workspaceTab: 'vendor-payments' })}
          />
        </div>
      ) : activeTab === 'maintenance' ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <MaintenanceArea focusItemId={maintenanceFocusItemId} />
        </div>
      ) : activeTab === 'settings' ? (
        <SettingsBackupTab />
      ) : (
        <ForecastTab onOpenBucketDeals={handleForecastBucketDrill} onOpenDeal={handleForecastOpenDeal} />
      )}

      <PasteImportDialog
        open={showPasteDialog}
        onOpenChange={setShowPasteDialog}
        onImport={handleBulkImport}
        fxRates={deal?.fx_mode === 'locked' ? deal.fx_snapshot : fxRates}
        existingItemCount={deal?.line_items?.length || 0}
        coreCurrency={deal?.core_currency || 'CHF'}
        loading={loading}
      />

      <ImportMasterPriceListDialog
        open={showImportMasterDialog}
        onOpenChange={setShowImportMasterDialog}
        dealId={deal?.id}
        onImported={(updatedDeal) => setDealWithUndo(updatedDeal)}
      />

      <Dialog
        open={downloadQuoteDialogOpen}
        onOpenChange={(open) => {
          if (downloadQuoteBusy) return;
          setDownloadQuoteDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-lg" data-testid="download-quote-pdf-dialog">
          <DialogHeader>
            <DialogTitle>Download quote PDF</DialogTitle>
            <DialogDescription>
              Client-ready PDF from this deal (net line amounts). Choose language and VAT treatment before download.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">Language</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`rounded-md border px-3 py-2 text-left text-sm ${downloadQuoteLang === 'fr' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  onClick={() => setDownloadQuoteLang('fr')}
                  disabled={downloadQuoteBusy}
                  data-testid="quote-lang-fr"
                >
                  <div className="font-medium">Français</div>
                  <div className={`mt-0.5 text-[11px] ${downloadQuoteLang === 'fr' ? 'text-gray-300' : 'text-gray-500'}`}>
                    Offre · labels FR
                  </div>
                </button>
                <button
                  type="button"
                  className={`rounded-md border px-3 py-2 text-left text-sm ${downloadQuoteLang === 'en' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  onClick={() => setDownloadQuoteLang('en')}
                  disabled={downloadQuoteBusy}
                  data-testid="quote-lang-en"
                >
                  <div className="font-medium">English</div>
                  <div className={`mt-0.5 text-[11px] ${downloadQuoteLang === 'en' ? 'text-gray-300' : 'text-gray-500'}`}>
                    Offer · labels EN
                  </div>
                </button>
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">VAT</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`rounded-md border px-3 py-2 text-left text-sm ${downloadQuoteVatMode === 'standard' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  onClick={() => setDownloadQuoteVatMode('standard')}
                  disabled={downloadQuoteBusy}
                  data-testid="quote-vat-mode-standard"
                >
                  <div className="font-medium">CH VAT 8.1%</div>
                  <div className={`mt-0.5 text-[11px] ${downloadQuoteVatMode === 'standard' ? 'text-gray-300' : 'text-gray-500'}`}>
                    Domestic — HT + TVA + TTC
                  </div>
                </button>
                <button
                  type="button"
                  className={`rounded-md border px-3 py-2 text-left text-sm ${downloadQuoteVatMode === 'exempt' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                  onClick={() => setDownloadQuoteVatMode('exempt')}
                  disabled={downloadQuoteBusy}
                  data-testid="quote-vat-mode-exempt"
                >
                  <div className="font-medium">VAT exempt</div>
                  <div className={`mt-0.5 text-[11px] ${downloadQuoteVatMode === 'exempt' ? 'text-gray-300' : 'text-gray-500'}`}>
                    International / export
                  </div>
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Default VAT for {String(downloadQuoteOptions?.currency || deal?.core_currency || 'CHF').toUpperCase()}:{' '}
              {downloadQuoteOptions?.default_vat_mode === 'exempt' ? 'VAT exempt (international)' : 'CH VAT 8.1%'}.
              Language defaults to Français.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800"
              onClick={() => setDownloadQuoteDialogOpen(false)}
              disabled={downloadQuoteBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={submitDownloadQuotePdf}
              disabled={downloadQuoteBusy}
              data-testid="download-quote-pdf-confirm"
            >
              {downloadQuoteBusy ? 'Preparing…' : 'Download PDF'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={issueQuoteDialogOpen}
        onOpenChange={(open) => {
          if (issueQuoteSubmitting) return;
          setIssueQuoteDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Issue quote to Bexio</DialogTitle>
            <DialogDescription>
              Choose how to create the quote in Bexio.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`rounded-md border px-3 py-2 text-sm ${issueQuoteMode === 'line_items' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                onClick={() => setIssueQuoteMode('line_items')}
                disabled={issueQuoteSubmitting}
              >
                Line-by-line
              </button>
              <button
                type="button"
                className={`rounded-md border px-3 py-2 text-sm ${issueQuoteMode === 'bulk_total' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                onClick={() => setIssueQuoteMode('bulk_total')}
                disabled={issueQuoteSubmitting}
              >
                Bulk total
              </button>
            </div>
            {issueQuoteMode === 'bulk_total' && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <label htmlFor="bulk-line-text" className="text-sm font-medium text-gray-900">
                    Quote title (optional)
                  </label>
                  <input
                    id="bulk-line-text"
                    type="text"
                    value={issueQuoteBulkText}
                    onChange={(e) => setIssueQuoteBulkText(e.target.value)}
                    placeholder="Optional title override for the Bexio quote"
                    className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm"
                    disabled={issueQuoteSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="bulk-note" className="text-sm font-medium text-gray-900">
                    Quote body text
                  </label>
                  <Textarea
                    id="bulk-note"
                    value={issueQuoteBulkNote}
                    onChange={(e) => setIssueQuoteBulkNote(e.target.value)}
                    placeholder="Text to place in Bexio quote body/header"
                    className="min-h-[90px]"
                    disabled={issueQuoteSubmitting}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800"
              onClick={() => setIssueQuoteDialogOpen(false)}
              disabled={issueQuoteSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={submitIssueToBexio}
              disabled={issueQuoteSubmitting}
            >
              {issueQuoteSubmitting ? 'Issuing…' : 'Issue to Bexio'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={issueContactDialogOpen}
        onOpenChange={(open) => {
          if (issueContactSubmitting) return;
          setIssueContactDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Bexio contact</DialogTitle>
            <DialogDescription>
              No Bexio contact matched this deal. Create one now and the quote will continue automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`rounded-md border px-3 py-2 text-sm ${issueContactForm.contact_type === 'company' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                onClick={() => setIssueContactForm((prev) => ({ ...prev, contact_type: 'company' }))}
                disabled={issueContactSubmitting}
              >
                Company
              </button>
              <button
                type="button"
                className={`rounded-md border px-3 py-2 text-sm ${issueContactForm.contact_type === 'person' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
                onClick={() => setIssueContactForm((prev) => ({ ...prev, contact_type: 'person' }))}
                disabled={issueContactSubmitting}
              >
                Person
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="bexio-contact-name-1" className="text-sm font-medium text-gray-900">
                  {issueContactForm.contact_type === 'person' ? 'Last name' : 'Company name'}
                </label>
                <input
                  id="bexio-contact-name-1"
                  type="text"
                  value={issueContactForm.name_1}
                  onChange={(e) => setIssueContactForm((prev) => ({ ...prev, name_1: e.target.value }))}
                  className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm"
                  disabled={issueContactSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="bexio-contact-name-2" className="text-sm font-medium text-gray-900">
                  {issueContactForm.contact_type === 'person' ? 'First name' : 'Additional name'}
                </label>
                <input
                  id="bexio-contact-name-2"
                  type="text"
                  value={issueContactForm.name_2}
                  onChange={(e) => setIssueContactForm((prev) => ({ ...prev, name_2: e.target.value }))}
                  className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm"
                  disabled={issueContactSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="bexio-contact-email" className="text-sm font-medium text-gray-900">
                  Email
                </label>
                <input
                  id="bexio-contact-email"
                  type="email"
                  value={issueContactForm.email}
                  onChange={(e) => setIssueContactForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm"
                  disabled={issueContactSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="bexio-contact-phone" className="text-sm font-medium text-gray-900">
                  Phone
                </label>
                <input
                  id="bexio-contact-phone"
                  type="text"
                  value={issueContactForm.phone_fixed}
                  onChange={(e) => setIssueContactForm((prev) => ({ ...prev, phone_fixed: e.target.value }))}
                  className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm"
                  disabled={issueContactSubmitting}
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <label htmlFor="bexio-contact-address" className="text-sm font-medium text-gray-900">
                  Street
                </label>
                <input
                  id="bexio-contact-address"
                  type="text"
                  value={issueContactForm.address}
                  onChange={(e) => setIssueContactForm((prev) => ({ ...prev, address: e.target.value }))}
                  className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm"
                  disabled={issueContactSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="bexio-contact-postcode" className="text-sm font-medium text-gray-900">
                  Postcode
                </label>
                <input
                  id="bexio-contact-postcode"
                  type="text"
                  value={issueContactForm.postcode}
                  onChange={(e) => setIssueContactForm((prev) => ({ ...prev, postcode: e.target.value }))}
                  className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm"
                  disabled={issueContactSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="bexio-contact-city" className="text-sm font-medium text-gray-900">
                  City / State
                </label>
                <input
                  id="bexio-contact-city"
                  type="text"
                  value={issueContactForm.city}
                  onChange={(e) => setIssueContactForm((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="Geneva"
                  className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm"
                  disabled={issueContactSubmitting}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800"
              onClick={() => setIssueContactDialogOpen(false)}
              disabled={issueContactSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={submitCreateBexioContact}
              disabled={issueContactSubmitting}
            >
              {issueContactSubmitting ? 'Creating…' : 'Create contact and continue'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={rejectDialogOpen}
        onOpenChange={(open) => {
          setRejectDialogOpen(open);
          if (!open) setRejectionComment('');
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Mark deal as Rejected</DialogTitle>
            <DialogDescription>
              Add a mandatory comment describing what was rejected and why.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="rejection-comment" className="text-sm font-medium text-gray-900">
              Rejection reason
            </label>
            <Textarea
              id="rejection-comment"
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
              placeholder="Example: Client declined quote due to budget mismatch and timeline risk."
              className="min-h-[120px]"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectionComment('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={submitRejectedStage}
              disabled={loading || !rejectionComment.trim()}
            >
              Save and reject
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="pointer-events-none fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] right-[max(0.75rem,env(safe-area-inset-right))] z-30 hidden sm:block">
        <img src="/evohome-logo.png" alt="EvoHome" className="h-4 w-auto opacity-90" />
      </div>

      <Toaster position="bottom-center" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  return getStoredToken() ? children : <Navigate to="/login" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={(
            <ProtectedRoute>
              <DealEngine />
            </ProtectedRoute>
          )}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
